const second = 1000;
const minute = 60 * second;
const hour = 60 * minute;
const day = 24 * hour;
const zonedTimestampPattern =
  /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})([+-])(\d{2}):(\d{2})$/;

function parseZonedTimestamp(value) {
  const match = zonedTimestampPattern.exec(value);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const date = Number(match[3]);
  const hours = Number(match[4]);
  const minutes = Number(match[5]);
  const seconds = Number(match[6]);
  const offsetHours = Number(match[8]);
  const offsetMinutes = Number(match[9]);
  const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();

  if (
    year < 1000 ||
    month < 1 ||
    month > 12 ||
    date < 1 ||
    date > daysInMonth ||
    hours > 23 ||
    minutes > 59 ||
    seconds > 59 ||
    offsetHours > 14 ||
    offsetMinutes > 59 ||
    (offsetHours === 14 && offsetMinutes !== 0)
  ) {
    return null;
  }

  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) ? timestamp : null;
}

function calendarDayNumber(timestamp, formatter) {
  const parts = Object.fromEntries(formatter.formatToParts(timestamp).map(({type, value}) => [type, value]));
  return Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day)) / day;
}

class PromotionCountdown extends HTMLElement {
  connectedCallback() {
    this.stopTimer();
    this.hidden = true;
    this.dataset.countdownInitialized = 'false';

    const startAt = this.dataset.startAt?.trim() || '';
    const endAt = this.dataset.endAt?.trim() || '';
    const serverTime = Number(this.dataset.serverSeconds) * 1000;
    const developmentPreview = this.dataset.developmentPreview === 'true';
    const previewDurationHours = Number(this.dataset.previewDurationHours);

    if (!Number.isFinite(serverTime)) return;

    if (developmentPreview) {
      if (!Number.isFinite(previewDurationHours) || previewDurationHours <= 0 || previewDurationHours > 168) return;
      this.startTime = serverTime;
      this.endTime = serverTime + previewDurationHours * hour;
    } else {
      this.startTime = parseZonedTimestamp(startAt);
      this.endTime = parseZonedTimestamp(endAt);
    }
    if (!Number.isFinite(this.clockOffset)) this.clockOffset = serverTime - Date.now();

    this.elements = {
      days: this.querySelector('[data-promotion-countdown-days]'),
      hours: this.querySelector('[data-promotion-countdown-hours]'),
      minutes: this.querySelector('[data-promotion-countdown-minutes]'),
      seconds: this.querySelector('[data-promotion-countdown-seconds]'),
      accessible: this.querySelector('[data-promotion-countdown-accessible]'),
      label: this.querySelector('.promotion-countdown__label'),
    };

    if (
      this.startTime === null ||
      this.endTime === null ||
      this.endTime <= this.startTime ||
      Object.values(this.elements).some((element) => !element)
    )
      return;

    this.calendarMode = this.dataset.messageMode === 'calendar';
    if (this.calendarMode) {
      this.calendarMessage = this.querySelector('[data-promotion-countdown-calendar]');
      if (!this.calendarMessage || !this.dataset.campaignTimezone?.trim()) return;
      if (![this.dataset.daysMessage, this.dataset.tomorrowMessage, this.dataset.todayMessage].every((text) => text?.trim())) return;
      try {
        this.calendarFormatter = new Intl.DateTimeFormat('en-US', {
          timeZone: this.dataset.campaignTimezone.trim(),
          calendar: 'gregory',
          numberingSystem: 'latn',
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        });
      } catch {
        return;
      }
      // The end is exclusive: a midnight end belongs to the preceding offer day.
      this.finalOfferDay = calendarDayNumber(this.endTime - 1, this.calendarFormatter);
    }

    this.dataset.countdownInitialized = 'true';
    this.update();
  }

  disconnectedCallback() {
    this.stopTimer();
  }

  stopTimer() {
    if (!this.timer) return;
    window.clearTimeout(this.timer);
    this.timer = null;
  }

  scheduleUpdate(delay) {
    this.stopTimer();
    this.timer = window.setTimeout(() => this.update(), delay);
  }

  update() {
    const now = Date.now() + this.clockOffset;
    if (now < this.startTime) {
      this.hidden = true;
      this.elements.accessible.textContent = '';
      this.scheduleUpdate(Math.min(this.startTime - now, minute));
      return;
    }

    const remaining = this.endTime - now;
    if (remaining <= 0) {
      this.hidden = true;
      this.elements.accessible.textContent = '';
      this.stopTimer();
      return;
    }

    if (this.calendarMode) {
      const daysRemaining = Math.max(0, this.finalOfferDay - calendarDayNumber(now, this.calendarFormatter));
      const template = daysRemaining === 0
        ? this.dataset.todayMessage
        : daysRemaining === 1
          ? this.dataset.tomorrowMessage
          : this.dataset.daysMessage;
      const message = template.replaceAll('{days}', String(daysRemaining));
      this.calendarMessage.textContent = message;
      this.elements.accessible.textContent = message;
      this.hidden = false;
      this.scheduleUpdate(Math.min(second, remaining));
      return;
    }

    const totalSeconds = Math.ceil(remaining / second);
    const values = {
      days: Math.floor(totalSeconds / (day / second)),
      hours: Math.floor((totalSeconds % (day / second)) / (hour / second)),
      minutes: Math.floor((totalSeconds % (hour / second)) / (minute / second)),
      seconds: totalSeconds % (minute / second),
    };

    for (const [part, value] of Object.entries(values)) {
      this.elements[part].textContent = String(value).padStart(2, '0');
    }

    this.hidden = false;
    this.elements.accessible.textContent = [
      this.elements.label.textContent.trim(),
      `${values.days} ${this.dataset.daysLabel}`,
      `${values.hours} ${this.dataset.hoursLabel}`,
      `${values.minutes} ${this.dataset.minutesLabel}`,
      `${values.seconds} ${this.dataset.secondsLabel}`,
    ].join(' ');

    this.scheduleUpdate(Math.min(second, remaining));
  }
}

if (!customElements.get('promotion-countdown')) {
  customElements.define('promotion-countdown', PromotionCountdown);
}
